// GreetingOverlay.js - iPhone-style greeting overlay
import React, { useEffect, useState, useRef, useCallback } from 'react';
import './GreetingOverlay.css';

/**
 * Props:
 *  - onFinish: function called when overlay fully dismissed
 *  - forceShow: boolean to always show (bypass localStorage)
 *  - durationMs: total visible time before automatic dismiss (default 3000)
 *  - storageKey: key used in localStorage (default 'portfolioGreetingSeen')
 */
const GreetingOverlay = ({
  onFinish = () => {},
  forceShow = false,
  durationMs = 5000,
  storageKey = 'portfolioGreetingSeen',
}) => {
  const [visible, setVisible] = useState(false);
  const [dismissing, setDismissing] = useState(false);
  const overlayRef = useRef(null);
  const touchStartY = useRef(null);
  const chimePlayedRef = useRef(false);
  const prefersReduced = usePrefersReducedMotion();

  // Show only if not seen before or forced
  useEffect(() => {
    try {
      const seen = localStorage.getItem(storageKey) === 'true';
      if (!seen || forceShow) {
        setVisible(true);
      }
    } catch (e) {
      setVisible(true);
    }
  }, []);

  // synthesize a short chime via WebAudio (no external file)
  const playChime = useCallback(() => {
    if (prefersReduced) return;
    try {
      const ctx = new (window.AudioContext || window.webkitAudioContext)();
      const now = ctx.currentTime;

      // simple two-tone pleasant chime
      const o1 = ctx.createOscillator();
      const o2 = ctx.createOscillator();
      const g = ctx.createGain();

      o1.type = 'sine';
      o2.type = 'sine';
      o1.frequency.setValueAtTime(880, now); // A5
      o2.frequency.setValueAtTime(1320, now); // E6
      g.gain.setValueAtTime(0, now);
      g.gain.linearRampToValueAtTime(0.12, now + 0.005);
      g.gain.exponentialRampToValueAtTime(0.001, now + 0.55);

      o1.connect(g);
      o2.connect(g);
      g.connect(ctx.destination);

      o1.start(now);
      o2.start(now + 0.02);
      o1.stop(now + 0.6);
      o2.stop(now + 0.6);

      // close context after a bit to release resources
      setTimeout(() => {
        ctx.close && ctx.close();
      }, 800);
    } catch (err) {
      // silent fallback
    }
  }, [prefersReduced]);

  // auto dismiss after duration (unless reduced motion -> we still wait but skip animations)
  useEffect(() => {
    if (!visible) return;

    const timer = setTimeout(() => {
      dismiss();
    }, durationMs);

    return () => clearTimeout(timer);
  }, [visible]);

  // play chime only after first user gesture (required for WebAudio autoplay policy)
  useEffect(() => {
    if (!visible || prefersReduced || chimePlayedRef.current) return;

    const playOnGesture = () => {
      if (chimePlayedRef.current) return;
      chimePlayedRef.current = true;
      playChime();
      cleanup();
    };

    const cleanup = () => {
      window.removeEventListener('pointerdown', playOnGesture);
      window.removeEventListener('touchstart', playOnGesture);
      window.removeEventListener('keydown', playOnGesture);
    };

    window.addEventListener('pointerdown', playOnGesture, { passive: true });
    window.addEventListener('touchstart', playOnGesture, { passive: true });
    window.addEventListener('keydown', playOnGesture);

    return cleanup;
  }, [visible, prefersReduced, playChime]);

  // dismiss sequence: add dismissing class then hide and call onFinish
  const dismiss = useCallback(() => {
    if (!visible || dismissing) return;
    setDismissing(true);

    // add class to trigger slide-up CSS animation
    if (overlayRef.current) {
      overlayRef.current.classList.add('dismiss');
    }

    // mark seen in localStorage
    try {
      localStorage.setItem(storageKey, 'true');
    } catch (e) {
      /* ignore */
    }

    // let animation complete (~650ms) then fully hide
    const wait = prefersReduced ? 0 : 700;
    setTimeout(() => {
      setVisible(false);
      setDismissing(false);
      onFinish && onFinish();
    }, wait);
  }, [visible, dismissing, onFinish, storageKey, prefersReduced]);

  // keyboard and click handlers
  useEffect(() => {
    if (!visible) return;
    const onKey = (e) => {
      if (e.key === 'Escape' || e.key === 'Enter' || e.key === ' ') {
        e.preventDefault();
        dismiss();
      }
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [visible, dismiss]);

  // touch handlers for swipe up
  const onTouchStart = (e) => {
    touchStartY.current = e.touches?.[0]?.clientY ?? null;
  };
  const onTouchEnd = (e) => {
    const endY = e.changedTouches?.[0]?.clientY ?? null;
    if (touchStartY.current !== null && endY !== null) {
      const delta = touchStartY.current - endY; // positive if swiped up
      if (delta > 60) {
        dismiss();
      }
    } else {
      // treat as tap
      dismiss();
    }
    touchStartY.current = null;
  };

  if (!visible) return null;

  return (
    <div
      ref={overlayRef}
      className="greeting-overlay"
      role="dialog"
      aria-modal="true"
      aria-label="Welcome message"
      onClick={dismiss}
      onTouchStart={onTouchStart}
      onTouchEnd={onTouchEnd}
    >
      <div
        className="greeting-card"
        onClick={(e) => e.stopPropagation()} /* clicking inside card should not dismiss immediately */
      >
        <div className="greeting-notch" aria-hidden="true" />
        <div className="greeting-content" aria-hidden={dismissing ? 'true' : 'false'}>
          <div className="greeting-line" role="heading" aria-level="1">
            Hello! Namasthe!
          </div>
          <div className="greeting-instruction" aria-hidden="true">
            <p>I'm Kowshik 👋- a full-stack developer and AI enthusiast.</p>
            <span style={{ fontWeight: 600 }}>Swipe up</span> to open — or tap / press Enter
          </div>
        </div>
      </div>

      {/* Swipe-up affordance — separate so tapping it dismisses */}
      <button
        className="swipe-up"
        onClick={(e) => {
          e.stopPropagation();
          dismiss();
        }}
        aria-label="Swipe up to open"
      >
        <div className="swipe-pill" role="button" tabIndex={0}>
          <span className="chev" aria-hidden="true" />
          <span className="swipe-text">Swipe up</span>
        </div>
      </button>
    </div>
  );
};

export default GreetingOverlay;

/* ---------- Hook: prefers reduced motion ---------- */
function usePrefersReducedMotion() {
  const [prefersReduced, setPrefersReduced] = useState(false);
  useEffect(() => {
    const mq = window.matchMedia && window.matchMedia('(prefers-reduced-motion: reduce)');
    const onChange = () => setPrefersReduced(!!(mq && mq.matches));
    onChange();
    if (mq && mq.addEventListener) {
      mq.addEventListener('change', onChange);
      return () => mq.removeEventListener('change', onChange);
    } else if (mq && mq.addListener) {
      mq.addListener(onChange);
      return () => mq.removeListener(onChange);
    }
    return undefined;
  }, []);
  return prefersReduced;
}
